/** @file Remembers the last drawn frame so draw() skips an unchanged one (the renderer's dirty flag). */
import type { Camera, RenderBuffers } from '@pose2d/geometry/types';

/** Memo of the last frame: chunk identities and versions, camera matrix and size, background. */
export interface FrameMemo {
  /** Records this frame; true when it differs from the recorded one or the memo was invalidated. */
  changed(chunks: readonly RenderBuffers[], camera: Camera, background: number): boolean;
  /** Makes the next `changed` return true (resize, page upload, context restore, a failed frame). */
  invalidate(): void;
}

/** View slots: worldToPixel[0..5], width, height, background. */
const VIEW_SLOTS = 9;

/** Creates an empty memo; the first frame always counts as changed. Allocation-free per frame. */
export function createFrameMemo(): FrameMemo {
  const seen: RenderBuffers[] = [];
  const versions: number[] = [];
  const view = new Float64Array(VIEW_SLOTS);
  let valid = false;
  const put = (i: number, v: number): boolean => {
    const same = Object.is(view[i], v);
    view[i] = v;
    return same;
  };
  const sameView = (camera: Camera, background: number): boolean => {
    let same = true;
    for (let i = 0; i < 6; i++) same = put(i, camera.worldToPixel[i] ?? 0) && same;
    same = put(6, camera.width) && same;
    same = put(7, camera.height) && same;
    return put(8, background) && same;
  };
  const sameChunks = (chunks: readonly RenderBuffers[]): boolean => {
    let same = seen.length === chunks.length;
    seen.length = chunks.length;
    versions.length = chunks.length;
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      if (chunk === undefined || (seen[i] === chunk && versions[i] === chunk.version)) continue;
      same = false;
      seen[i] = chunk;
      versions[i] = chunk.version;
    }
    return same;
  };
  return {
    changed(chunks, camera, background) {
      const viewSame = sameView(camera, background);
      const chunksSame = sameChunks(chunks);
      const result = !(valid && viewSame && chunksSame);
      valid = true;
      return result;
    },
    invalidate() {
      valid = false;
    },
  };
}
